<template>
  <div class="mc-table">
    <div class="card">
      <div class="card-body">
        <div class="d-flex justify-content-end flex-grow-1 gap-2 flex-wrap">
          <slot
            name="viewManager"
            :handleSetDataFields="handleSetDataFields"
            :dataFields="dataFields"
            :initialFields="initialFields"
          />
          <b-input-group v-if="searchable" size="sm" class="mc-has-search" @click="focusSearch">
            <template #prepend>
              <i class="ps-2 mc-icon icon-fill_search"></i>
            </template>
            <b-form-input
              type="search"
              :placeholder="`${$t('global.search')}...`"
              :disabled="isBusy"
              :value="search"
              @input="handleSearchChange"
              ref="searchInput"
            ></b-form-input>
          </b-input-group>
        </div>

        <div class="table-responsive border border-1 mc-table-overflow">
          <b-table-simple>
            <b-thead>
              <b-tr>
                <b-th
                  v-for="field in computedTopHeaderFields"
                  :key="field.key"
                  :rowspan="field.rowspan || 1"
                  :colspan="field.colspan || 1"
                >
                  <div>{{ field.label }}</div>
                </b-th>
              </b-tr>
              <b-tr>
                <b-th
                  v-for="field in computedBottomHeaderFields"
                  :key="field.key"
                  :rowspan="field.rowspan || 1"
                  :colspan="field.colspan || 1"
                >
                  <div>{{ field.label }}</div>
                </b-th>
              </b-tr>
            </b-thead>
            <b-tbody>
              <b-tr v-if="isBusy">
                <b-td :colspan="computedBodyFields.length">
                  <div class="__busy">
                    <b-spinner class="align-middle mb-2"></b-spinner>
                    <strong>{{ $t('global.text.loading') }}...</strong>
                  </div>
                </b-td>
              </b-tr>

              <template v-else>
                <b-tr v-for="item in items" :key="item.id">
                  <b-td v-for="field in computedBodyFields">
                    <div>
                      {{
                        field.format
                          ? field.format(get(item, field.key))
                          : get(item, field.key) || '-'
                      }}
                    </div>
                  </b-td>
                </b-tr>
              </template>
            </b-tbody>
          </b-table-simple>
        </div>

        <div class="d-flex flex-wrap flex-md-nowrap">
          <div class="mc-limit">
            <div class="__limit">
              <div>{{ $t('global.show') }}</div>
              <b-form-select
                class="form-select form-select-sm __limit--dropdown"
                size="sm"
                :disabled="isBusy"
                :options="limitOptions"
                :value="limit"
                @input="handleLimitChange"
              >
              </b-form-select>
              <div>{{ $t('global.object.data').toLowerCase() }}.</div>
            </div>

            <div class="__info">
              <strong>{{ startData }}</strong> - <strong>{{ endData }}</strong>
              {{ $t('global.object.of') }} <strong>{{ totalCount }}</strong
              >.
            </div>
          </div>
          <div class="mc-pagination-container">
            <b-pagination
              :disabled="isBusy"
              :per-page="limit"
              :total-rows="totalCount"
              :value="page"
              @change="handlePageChange"
            ></b-pagination>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { debounce } from '@/helpers/debounce'
import get from 'lodash/get'
import cloneDeep from 'lodash/cloneDeep'
import flattenDeep from 'lodash/flattenDeep'

const cloneFields = (fields) =>
  cloneDeep(fields).map((field) => {
    if (field.fields) {
      const subFields = field.fields.map((item) => ({ ...item, shown: true }))
      field.fields = subFields
    }

    return { ...field, shown: true }
  })

export default {
  props: {
    fields: {
      type: Array,
      default: () => [],
    },
    apiClient: {
      type: Object,
      default: () => {},
    },
    limitOptions: {
      default: () => [10, 25, 50, 100],
    },
    searchable: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      page: 1,
      limit: 10,
      totalCount: 0,
      search: '',

      isBusy: true,

      items: [],
      sorts: [],
      ids: [],
      dataFields: cloneFields(this.fields),
    }
  },
  computed: {
    computedTopHeaderFields() {
      const fields = []
      this.dataFields.forEach((item) => {
        if (!item.shown) return

        const obj = Object.assign({}, item)

        if (item.fields) {
          delete obj.fields
        }
        fields.push(obj)
      })

      return fields
    },
    computedBottomHeaderFields() {
      const fields = []
      this.dataFields.forEach((item) => {
        if (!item.shown || !item.fields) return

        item.fields.forEach((el) => {
          if (!el.shown) return
          fields.push(el)
        })
      })

      return flattenDeep(fields)
    },
    computedBodyFields() {
      const fields = []
      this.dataFields.forEach((item) => {
        if (!item.shown) return

        if (item.fields) {
          const newFields = item.fields.filter((el) => el.shown)
          fields.push(cloneDeep(newFields))
        } else {
          if (!item.shown) return

          const obj = Object.assign({}, item)
          fields.push(obj)
        }
      })

      return flattenDeep(fields)
    },
    startData() {
      return this.limit * this.page - this.limit + 1
    },
    endData() {
      const _endData = this.limit * this.page
      return this.totalCount < _endData ? this.totalCount : _endData
    },
    initialFields() {
      return cloneFields(this.fields)
    },
  },
  async mounted() {
    await this.$nextTick()
    await this.getData({
      limit: this.limit,
      page: this.page,
    })
  },
  methods: {
    focusSearch() {
      this.$refs.searchInput.focus()
    },

    get,
    async getData(opts) {
      this.isBusy = true
      const res = await this.apiClient.get(opts)
      if (!res || res?.errors) return
      this.items = res.data.paginated_result
      this.ids = res.data.ids
      this.page = res.metadata.page
      this.totalCount = res.metadata.total_count
      this.$emit('available-change', true)

      if (res.data.paginated_result.length === 0 && !this.search) {
        this.$emit('available-change', false)
      }

      this.isBusy = false
    },
    handleSearchChange: debounce(async function (_search) {
      this.search = _search
      const opts = {
        limit: this.limit,
        search: _search != '' ? _search : undefined,
        page: 1,
        sorts: this.sorts.map((sort) => `${sort.key} ${sort.order}`),
      }
      await this.getData(opts)
      this.$refs['search-input-ref'].$el.focus()
    }, 500),
    handleClearSearch() {
      this.handleSearchChange(undefined)
    },
    async handleLimitChange(_limit) {
      this.limit = _limit

      await this.getData({
        limit: _limit,
        search: this.search != '' ? this.search : undefined,
        page: 1,
        sorts: this.sorts.map((sort) => `${sort.key} ${sort.order}`),
      })
    },
    async handlePageChange(newPage) {
      const opts = {
        page: newPage,
        search: this.search != '' ? this.search : undefined,
        limit: this.limit,
        sorts: this.sorts.map((sort) => `${sort.key} ${sort.order}`),
      }
      await this.getData(opts)
    },
    handleSetDataFields(fields) {
      this.dataFields = cloneDeep(fields)
    },
  },
}
</script>

<style lang="scss" scoped>
@import './style.scss';
</style>
